/**
 * Sandbox mode detection.
 *
 * The sandbox is a SELF-CONTAINED demo build of the app: every request is served
 * from seeded data in IndexedDB, nothing reaches the backend, and all writes are
 * discarded on reload. It exists so anyone can click through the product with
 * realistic data and no account, no WABA and no risk of touching real records.
 *
 * 🔴 THIS MODULE MUST STAY SYNCHRONOUS AND IMPORT-FREE.
 * `main.tsx` calls `isSandboxMode()` before `createRoot`, and the whole sandbox
 * bundle is behind that branch. Importing anything from `@/lib` here would drag
 * the app's module graph into the decision and defeat the tree-shake that keeps
 * the sandbox out of the production build.
 *
 * Three independent switches, checked in order of authority:
 *
 *  1. `VITE_SANDBOX=true` — the BUILD-TIME flag, and the one that matters in
 *     production. A sandbox deployment is its own artifact on its own URL, so a
 *     production build literally cannot be flipped into sandbox mode by a query
 *     param or a hostname someone points at it.
 *  2. Hostname — lets ONE artifact serve both (e.g. `sandbox.example.com`).
 *     Configured via `VITE_SANDBOX_HOSTS`, plus the `sandbox.`/`demo.` prefixes
 *     and `*.sandbox.*` by convention.
 *  3. `?sandbox=1` — local development only, so a developer can exercise the
 *     sandbox against a normal `npm run dev` without a second build. Deliberately
 *     IGNORED once `VITE_SANDBOX` is false in a production build (see below) —
 *     otherwise the real app would grow a URL that silently fakes every response,
 *     which is indistinguishable from a catastrophic data bug to whoever opens it.
 */

/** True when this bundle was built as the dedicated sandbox artifact. */
const BUILD_FLAG = String(import.meta.env.VITE_SANDBOX ?? '').toLowerCase() === 'true';

/**
 * Hostnames that should run in sandbox mode. Comma-separated, exact matches,
 * case-insensitive. Lets a single deployment serve the real app and the sandbox
 * from two DNS names.
 */
const CONFIGURED_HOSTS = String(import.meta.env.VITE_SANDBOX_HOSTS ?? '')
  .split(',')
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);

/** `DEV` is Vite's own build-mode flag — false in every production bundle. */
const IS_DEV_BUILD = Boolean(import.meta.env.DEV);

function hostnameLooksLikeSandbox(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (CONFIGURED_HOSTS.includes(host)) return true;
  // Conventional prefix: sandbox.app.com, sandbox-app.com.
  //
  // 🔴 "demo" is DELIBERATELY NOT part of this convention. It used to be
  // (`/^(sandbox|demo)[.-]/`), and that silently matched the pre-existing real
  // environment `demo.wenext.ai` — a genuine deployment with a real backend and
  // real user accounts, unrelated to this client-only sandbox. The moment this
  // module shipped, every page load on demo.wenext.ai satisfied `isSandboxMode()`
  // and `bootstrapSandboxSession()` force-overwrote `auth_token`/`user` in
  // localStorage with the fake sandbox identity — so real users could not log in,
  // and logging out just reloaded into the sandbox identity again. A real "demo"
  // style environment must opt in explicitly via `VITE_SANDBOX_HOSTS`, never by
  // hostname convention alone.
  if (/^sandbox[.-]/.test(host)) return true;
  // A sandbox label anywhere in the name: app.sandbox.example.com.
  if (/(^|\.)sandbox(\.|$)/.test(host)) return true;
  return false;
}

/**
 * Is this page running as the sandbox?
 *
 * Safe to call from anywhere, including before React mounts and in a non-DOM
 * environment (tests, SSR) where it resolves purely from the build flag.
 */
export function isSandboxMode(): boolean {
  if (BUILD_FLAG) return true;

  if (typeof window === 'undefined') return false;

  if (window.location?.hostname && hostnameLooksLikeSandbox(window.location.hostname)) {
    return true;
  }

  // Dev-only escape hatch. Gated on the build mode, NOT on the hostname, so it
  // can never be reached in a deployed production artifact.
  if (IS_DEV_BUILD) {
    try {
      const params = new URLSearchParams(window.location.search);
      const flag = (params.get('sandbox') ?? '').toLowerCase();
      if (flag === '1' || flag === 'true') return true;
      // Sticky for the tab, so client-side navigation (which drops the query
      // string) doesn't fall back to the real API mid-session.
      if (sessionStorage.getItem(SANDBOX_SESSION_KEY) === '1') return true;
    } catch {
      // No sessionStorage (private mode / blocked storage) — the query param
      // branch above still works, it just won't persist across navigation.
    }
  }

  return false;
}

/** Marks the tab as sandboxed so the dev `?sandbox=1` flag survives navigation. */
export const SANDBOX_SESSION_KEY = 'wenext_sandbox_mode';

/**
 * Remember the dev-mode opt-in for the rest of the tab's life. No-op unless the
 * query param is actually present, so it can never turn a normal tab sandboxed.
 */
export function persistSandboxOptIn(): void {
  if (!IS_DEV_BUILD || typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const flag = (params.get('sandbox') ?? '').toLowerCase();
    if (flag === '1' || flag === 'true') {
      sessionStorage.setItem(SANDBOX_SESSION_KEY, '1');
    }
  } catch {
    // Storage unavailable — nothing to persist, not an error.
  }
}
